import React from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { HouseIcon as House, DogIcon as Dog, PawPrintIcon as PawPrint, TreeStructureIcon as TreeStructure, UsersFourIcon as UsersFour, CalendarIcon as Calendar, CurrencyDollarIcon as CurrencyDollar, ReceiptIcon as Receipt, SignOutIcon as SignOut, UserCircleIcon as UserCircle } from "@phosphor-icons/react";
import { isNativeApp } from "@/lib/nativeBridge";
import BackButton from "@/components/BackButton";
import FooterQuickLinks from "@/components/FooterQuickLinks";

/**
 * Layout that works for both authed and anonymous visitors.
 * Used for public-facing routes like /events.
 */
export default function PublicShell({ children }) {
  const { user, logout } = useAuth();

  const handleLogout = () => {
    // logout() owns the hard navigation back to the landing page —
    // see contexts/AuthContext.jsx for the race-condition rationale.
    logout();
  };

  const authedItems = [
    { to: "/dashboard", label: "Dashboard", icon: House },
    { to: "/dogs", label: "Dogs", icon: Dog },
    { to: "/litters", label: "Litters", icon: PawPrint },
    { to: "/breeders", label: "Breeders", icon: UsersFour },
    { to: "/pedigree", label: "Pedigree", icon: TreeStructure },
    { to: "/events", label: "Events", icon: Calendar },
    isNativeApp()
      ? { to: "/billing", label: "My Account", icon: UserCircle }
      : { to: "/billing", label: "Billing", icon: CurrencyDollar },
  ];
  if (user?.role === "admin") authedItems.push({ to: "/admin/payments", label: "Admin", icon: Receipt });

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-ink font-manrope">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E5E5E5]/50" data-testid="public-header">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-10">
            <NavLink to={user ? "/dashboard" : "/"} className="flex items-center gap-2 min-w-0" data-testid="brand-link">
              <img
                src="/logo-256.png"
                alt="Armstrong K9 Registry"
                className="h-10 w-10 flex-shrink-0 rounded-full object-contain"
              />
              <span className="font-spectral text-sm sm:text-base tracking-tight leading-tight whitespace-nowrap hidden sm:inline text-ink">
                Armstrong K9 Registry
              </span>
            </NavLink>
            {user && (
              <nav className="hidden md:flex items-center gap-1">
                {authedItems.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    data-testid={`nav-${it.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
                        isActive ? "bg-[#0E0E0E]/5 text-[#0E0E0E]" : "text-[#6B6B6B] hover:bg-[#FAFAFA]"
                      }`
                    }
                  >
                    <it.icon size={18} />
                    {it.label}
                  </NavLink>
                ))}
              </nav>
            )}
            {!user && (
              <nav className="hidden md:flex items-center gap-1">
                <NavLink to="/marketplace" className={({ isActive }) => `px-4 py-2 rounded-full text-sm ${isActive ? "bg-[#0E0E0E]/5 text-[#0E0E0E]" : "text-[#6B6B6B] hover:bg-[#FAFAFA]"}`} data-testid="public-marketplace-nav">
                  Marketplace
                </NavLink>
                <NavLink to="/breeds" className={({ isActive }) => `px-4 py-2 rounded-full text-sm ${isActive ? "bg-[#0E0E0E]/5 text-[#0E0E0E]" : "text-[#6B6B6B] hover:bg-[#FAFAFA]"}`} data-testid="public-breeds-nav">
                  Breeds
                </NavLink>
                <NavLink to="/events" className={({ isActive }) => `px-4 py-2 rounded-full text-sm ${isActive ? "bg-[#0E0E0E]/5 text-[#0E0E0E]" : "text-[#6B6B6B] hover:bg-[#FAFAFA]"}`} data-testid="public-events-nav">
                  Events
                </NavLink>
                <NavLink to="/journal" className={({ isActive }) => `px-4 py-2 rounded-full text-sm ${isActive ? "bg-[#0E0E0E]/5 text-[#0E0E0E]" : "text-[#6B6B6B] hover:bg-[#FAFAFA]"}`} data-testid="public-journal-nav">
                  Journal
                </NavLink>
              </nav>
            )}
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <span className="hidden sm:block text-sm text-[#6B6B6B]" data-testid="user-name">{user.name}</span>
                <Button onClick={handleLogout} variant="outline" size="sm" className="rounded-full border-[#E5E5E5]" data-testid="logout-btn">
                  <SignOut size={16} className="mr-1" />Sign out
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="public-login-link">
                  <Button variant="outline" size="sm" className="rounded-full border-[#E5E5E5]">Sign in</Button>
                </Link>
                <Link to="/register" data-testid="public-register-link">
                  <Button size="sm" className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white">Get started</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto p-6 md:p-10">
        <BackButton fallback="/" />
        {children}
      </main>
      <footer className="border-t border-[#E5E5E5]/50 bg-white/50 mt-10">
        <FooterQuickLinks tone="light" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-col gap-3 text-xs text-[#6B6B6B]">
          <p
            className="text-[11px] leading-relaxed text-[#6B6B6B] italic max-w-3xl"
            data-testid="footer-venue-disclaimer"
          >
            Armstrong K9 Registry is a <strong>venue</strong> connecting
            buyers, sellers, and breeders. The Registry is not the seller and
            does not warrant the health, lineage, temperament, or fitness of any
            animal. Sellers are independent merchants of record for their own
            sales and taxes. See{" "}
            <NavLink to="/seller-standards" className="underline hover:text-ink">Seller standards</NavLink>{" "}
            and{" "}
            <NavLink to="/terms" className="underline hover:text-ink">Terms of Service</NavLink>.
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p>
              © {new Date().getFullYear()} Armstrong K9 Registry
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <NavLink to="/features" className="hover:text-ink">Features</NavLink>
              <NavLink to="/journal" className="hover:text-ink" data-testid="footer-journal">Journal</NavLink>
              <NavLink to="/seller-standards" className="hover:text-ink" data-testid="footer-seller-standards">Seller standards</NavLink>
              <NavLink to="/terms" className="hover:text-ink">Terms</NavLink>
              <NavLink to="/privacy" className="hover:text-ink">Privacy</NavLink>
              <NavLink to="/contact" className="hover:text-ink">Contact</NavLink>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
